import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout.js";
import { Button } from "../components/ui.js";
import { auth } from "../copy/auth.js";
import { common } from "../copy/common.js";
import { useSession, type SessionUser } from "../session.js";

function recoveryFileText(displayName: string, code: string): string {
  return [
    auth.recovery.fileHeading,
    "",
    auth.recovery.fileName(displayName),
    auth.recovery.fileCode(code),
    "",
    auth.recovery.fileBody,
    auth.recovery.fileOnce,
    "",
  ].join("\n");
}

export function RecoveryScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useSession();
  const state = location.state as { code?: string; user?: SessionUser } | null;
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  if (!state?.code || !state.user) return <Navigate to="/" replace />;
  const { code, user } = state;

  const copyCode = async () => {
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyError(true);
    }
  };

  const saveFile = () => {
    const blob = new Blob([recoveryFileText(user.display_name, code)], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "vocifuchs-code.txt";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <AuthLayout mood="think" title={auth.recovery.title} intro={auth.recovery.intro}>
      <div className="space-y-7">
        <p className="rounded-3xl border-2 border-dashed border-fox/40 bg-paper-2 px-4 py-7 text-center font-mono text-2xl tracking-[0.18em] break-all sm:text-3xl">
          {code}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Button variant="ghost" block onClick={() => void copyCode()}>
            {copied ? common.copied : common.copy}
          </Button>
          <Button variant="ghost" block onClick={saveFile}>
            {auth.recovery.saveFile}
          </Button>
        </div>
        {copyError ? (
          <p className="text-sm text-rose" role="status">
            {auth.recovery.copyFailed}
          </p>
        ) : null}

        <Button
          onClick={() => {
            setUser(user);
            navigate("/", { replace: true });
          }}
        >
          {auth.recovery.confirm}
        </Button>
      </div>
    </AuthLayout>
  );
}
